/**
 * TrayDownloadPage — Clickable tech pack download for PCBWay / contract manufacturers.
 * Route: /tray-download
 */
import { Download, FileText, Cpu, Package, Layers, Shield, Cog, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const FILES = [
  {
    name: 'PCBWay Tech Pack v2',
    file: '/tray-pack/01-pcbway-tech-pack-v2.md',
    size: '18.9 KB',
    icon: FileText,
    accent: 'text-[#F7931A]',
    bg: 'bg-[#F7931A]/10',
    tag: 'QUOTE DOC',
    desc: 'Primary document for PCBWay. BOM, PCB spec, enclosure dims, compliance, budget bands, quote request template.',
  },
  {
    name: 'Lego Tray Design Spec',
    file: '/tray-pack/02-lego-tray-design.md',
    size: '20.5 KB',
    icon: Layers,
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    tag: 'DESIGN',
    desc: 'Full architecture: 6-slot modular tray, NAU7802 I2C load-cell chain, All-Masters ESP-NOW mesh, LiPo UPS, staff lock.',
  },
  {
    name: 'Sourcing Sheet',
    file: '/tray-pack/03-sourcing-sheet.md',
    size: '8.6 KB',
    icon: Package,
    accent: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    tag: 'BOM',
    desc: 'Component list with approved manufacturers, part numbers, unit cost, qty per tray, lead times. Send verbatim to sourcing.',
  },
  {
    name: 'Shrinkage Prevention Architecture',
    file: '/tray-pack/04-shrinkage-prevention.md',
    size: '9.3 KB',
    icon: Shield,
    accent: 'text-purple-400',
    bg: 'bg-purple-500/10',
    tag: 'CONTEXT',
    desc: 'Why every hardware choice exists — 5-layer loss-prevention model. Gives the EE / ME team business context.',
  },
  {
    name: 'Enclosure CAD (OpenSCAD)',
    file: '/tray-pack/05-lego-tray-v2.scad',
    size: '7.9 KB',
    icon: Cog,
    accent: 'text-amber-400',
    bg: 'bg-amber-500/10',
    tag: 'CAD',
    desc: 'Parametric OpenSCAD source. Open in OpenSCAD 2024.01+ → Export STL/STEP for the mechanical quote.',
  },
  {
    name: 'Reference Firmware',
    file: '/tray-pack/06-lego-tray-v2.0.ino',
    size: '16.7 KB',
    icon: Cpu,
    accent: 'text-pink-400',
    bg: 'bg-pink-500/10',
    tag: 'FIRMWARE',
    desc: 'ESP32 Arduino sketch. NAU7802 drivers, ESP-NOW leader election, LiPo UPS FSM, staff-lock GPIO. Builds out-of-the-box.',
  },
  {
    name: 'Firmware README',
    file: '/tray-pack/07-firmware-README.md',
    size: '2.2 KB',
    icon: FileText,
    accent: 'text-[#94a3b8]',
    bg: 'bg-white/5',
    tag: 'README',
    desc: 'Build & flash instructions for the reference firmware. Arduino IDE 2.x + ESP32 board package.',
  },
];

const PDF_FILE = {
  href: '/lego-tray-v2-pcbway-summary.pdf',
  size: '26 KB',
  name: 'lego-tray-v2-pcbway-summary.pdf',
};

const ZIP_FILE = {
  href: '/lego-tray-v2-pcbway-pack.zip',
  size: '58 KB',
  name: 'lego-tray-v2-pcbway-pack.zip',
};

export default function TrayDownloadPage() {
  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="tray-download-page">
      {/* Top bar */}
      <header className="border-b border-[#1a2235] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-xs text-[#94a3b8] hover:text-white flex items-center gap-1" data-testid="back-home">
            <ArrowLeft className="w-3 h-3" /> getbitsy
          </Link>
          <Badge className="bg-[#F7931A]/10 text-[#F7931A] border border-[#F7931A]/30 text-[10px]">
            CONFIDENTIAL · NDA RECOMMENDED
          </Badge>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-10 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F7931A] to-amber-600 flex items-center justify-center">
            <Package className="w-6 h-6 text-black" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748b]">M.O.A.T Hardware</p>
            <h1 className="text-2xl sm:text-3xl font-bold">Lego Tray v2 — Tech Pack</h1>
          </div>
        </div>
        <p className="text-sm text-[#94a3b8] max-w-2xl leading-relaxed">
          Everything PCBWay (or any contract manufacturer) needs to quote a prototype run of the 6-slot modular dry tray.
          Download the full bundle as a ZIP, or pull individual files below.
        </p>

        {/* Primary PDF CTA */}
        <a
          href={PDF_FILE.href}
          download
          className="mt-6 inline-flex items-center gap-3 bg-[#F7931A] text-black font-semibold px-5 py-3 rounded-xl hover:bg-[#F7931A]/90 transition shadow-lg shadow-[#F7931A]/20"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4" />
          Download one-page PDF (.pdf · {PDF_FILE.size})
        </a>
        <p className="text-[10px] text-[#64748b] mt-2 font-mono">{PDF_FILE.name} — attach this to your email</p>

        {/* Secondary ZIP CTA */}
        <a
          href={ZIP_FILE.href}
          download
          className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-3 bg-[#0d1220] border border-[#1a2235] text-white font-medium px-5 py-3 rounded-xl hover:border-[#F7931A]/40 transition"
          data-testid="download-zip-btn"
        >
          <Download className="w-4 h-4" />
          Full pack (.zip · {ZIP_FILE.size})
        </a>
        <p className="text-[10px] text-[#64748b] mt-2 font-mono">{ZIP_FILE.name} — CAD, firmware, full BOM</p>
      </section>

      {/* File grid */}
      <section className="px-6 pb-10 max-w-5xl mx-auto">
        <h2 className="text-sm font-semibold text-[#94a3b8] uppercase tracking-wider mb-4">Individual files</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {FILES.map((f) => {
            const Icon = f.icon;
            return (
              <a
                key={f.file}
                href={f.file}
                download
                className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-4 hover:border-[#F7931A]/40 transition group block"
                data-testid={`download-${f.file.split('/').pop()}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-5 h-5 ${f.accent}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-sm font-semibold truncate">{f.name}</p>
                      <Badge className={`${f.bg} ${f.accent} text-[9px] border-0 shrink-0`}>
                        {f.tag}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-[#94a3b8] leading-relaxed">{f.desc}</p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-[10px] text-[#64748b] font-mono">{f.size}</p>
                      <div className="text-[10px] text-[#F7931A] opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
                        <Download className="w-3 h-3" /> Download
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </section>

      {/* How to send */}
      <section className="px-6 pb-16 max-w-5xl mx-auto">
        <div className="bg-[#0d1220] border border-[#1a2235] rounded-2xl p-5">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <span className="w-6 h-6 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <FileText className="w-3 h-3 text-cyan-400" />
            </span>
            How to send to PCBWay
          </h3>
          <ol className="text-xs text-[#94a3b8] space-y-2 pl-4 list-decimal">
            <li>Reply to their sales email. Attach <span className="font-mono text-white">{PDF_FILE.name}</span> as the primary document.</li>
            <li>Attach <span className="font-mono text-white">{ZIP_FILE.name}</span> as supporting pack (CAD, firmware, full BOM).</li>
            <li>Request a quote for <span className="text-white">50 units</span> (prototype batch) with a 4-week target.</li>
            <li>Ask for the mechanical (enclosure) and electrical (PCB + assembly) line items to be quoted separately.</li>
            <li>Note that <span className="text-white">05-lego-tray-v2.scad</span> needs to be opened in OpenSCAD → export STEP/STL for the ME team.</li>
          </ol>
          <p className="text-[10px] text-[#64748b] mt-4">
            Tip: If PCBWay asks for an NDA first, we can draft one — just let us know.
          </p>
        </div>
      </section>
    </div>
  );
}
